import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Clock, DollarSign, Target, TrendingUp, CheckCircle, Zap, Users, Settings } from "lucide-react";

const FrancePage = () => {
  const painPoints = [
    {
      icon: <DollarSign className="h-8 w-8 text-orange-500" />,
      title: "Budget Google Ads Gaspillé",
      description: "Vous payez pour des mots-clés qui ne convertissent pas. Les entreprises françaises perdent 40-60% de leur budget sur des campagnes inefficaces."
    },
    {
      icon: <Target className="h-8 w-8 text-orange-500" />,
      title: "Mauvaise Stratégie de Mots-clés",
      description: "Les mots-clés français ont des exigences spécifiques. Les approches génériques ne fonctionnent pas pour le marché B2B local."
    },
    {
      icon: <Clock className="h-8 w-8 text-orange-500" />,
      title: "Trop de Temps pour Commencer",
      description: "Google Ads est compliqué. Les agences prennent des semaines pour la configuration, tandis que vous attendez des résultats."
    }
  ];

  const features = [
    {
      icon: <Zap className="h-8 w-8 text-blue-500" />,
      title: "Campagnes Budget-Intelligentes",
      description: "Filtrage intelligent des mots-clés pilotés par l'IA pour les marchés français. Payez uniquement pour les demandes de haute qualité."
    },
    {
      icon: <Target className="h-8 w-8 text-blue-500" />,
      title: "Analyse Locale des Mots-clés",
      description: "Vrais enchères Google Ads pour les mots-clés français. Comprenez exactement ce que chaque mot-clé coûte et apporte."
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-blue-500" />,
      title: "Optimisation Automatique",
      description: "Les campagnes s'ajustent quotidiennement. Meilleurs taux de conversion avec moins d'effort manuel."
    },
    {
      icon: <Users className="h-8 w-8 text-blue-500" />,
      title: "Support en Français",
      description: "Support complet en français. Pas de barrières linguistiques pour les questions techniques."
    }
  ];

  const steps = [
    {
      number: "1",
      title: "Parlez-nous de votre Entreprise",
      description: "Partagez les détails de votre entreprise, vos clients cibles et votre budget. Seulement 2 minutes de configuration."
    },
    {
      number: "2",
      title: "L'IA Crée vos Campagnes",
      description: "Upfixe analyse le contenu de votre site et crée des campagnes professionnelles optimisées pour le français."
    },
    {
      number: "3",
      title: "Plus de Clients, Moins de Gaspillage",
      description: "Voyez des demandes très qualifiées. L'intelligence budgétaire signifie un succès plus rapide."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Google Ads pour Entreprises Françaises | Automatisation Budget-Smart | Upfixe</title>
        <meta name="description" content="Google Ads budget-intelligents pour entreprises françaises. Filtrage intelligent des mots-clés, optimisation locale, support français. Commencez gratuitement." />
        <meta name="keywords" content="Google Ads France, Agence Google Ads, Optimisation Mots-clés, Optimisation Budget, Campagnes Françaises" />
        <meta property="og:title" content="Google Ads pour Entreprises Françaises | Automatisation Budget-Smart | Upfixe" />
        <meta property="og:description" content="Google Ads budget-intelligents pour entreprises françaises. Filtrage intelligent des mots-clés, optimisation locale, support français." />
        <link rel="canonical" href="https://upfixe.com/fr" />
        <link rel="alternate" hrefLang="x-default" href="https://upfixe.com/" />
        <link rel="alternate" hrefLang="en" href="https://upfixe.com/" />
        <link rel="alternate" hrefLang="de" href="https://upfixe.com/de" />
        <link rel="alternate" hrefLang="fr" href="https://upfixe.com/fr" />
        <link rel="alternate" hrefLang="en-GB" href="https://upfixe.com/uk" />
        <link rel="alternate" hrefLang="en-AU" href="https://upfixe.com/au" />
      </Helmet>
      <Header />

      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          {/* Hero Section */}
          <div className="text-center mb-20">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Arrêtez de gaspiller <br />
              <span className="text-blue-600">le budget sur des mots-clés inefficaces.</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Google Ads pour entreprises françaises qui fonctionnent vraiment. Automatisation intelligente du budget. Pas d'agences coûteuses. Pas de complications.
            </p>
            <div className="flex justify-center mb-6">
              <Button
                size="lg"
                className="text-lg px-8 h-14 bg-blue-600 hover:bg-blue-700 font-semibold"
                asChild
              >
                <Link to="/auth?signup=true">Commencer Gratuitement</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">Aucune carte bancaire • Support français • Résultats professionnels</p>
          </div>

          {/* Problem Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Entreprises Françaises: Pourquoi Google Ads est Difficile</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Les mots-clés français sont chers. Les outils de ciblage génériques ne fonctionnent pas pour notre marché B2B local.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {painPoints.map((point, index) => (
                <Card key={index} className="text-center p-6 border-orange-200 bg-orange-50/50">
                  <div className="flex justify-center mb-4">{point.icon}</div>
                  <h3 className="text-xl font-semibold mb-3">{point.title}</h3>
                  <p className="text-muted-foreground">{point.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Solution Section */}
          <div className="mb-20 bg-blue-50 p-12 rounded-2xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-6">Upfixe: Intelligence Budgétaire pour la France</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Vrais enchères Google Ads. Intelligence des mots-clés français. Optimisation budgétaire automatique qui fonctionne.
              </p>
            </div>
          </div>

          {/* Features Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Pourquoi les Agences Françaises Choisissent Upfixe</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Outils intelligents pour les marchés français. Aucune complexité. Juste des résultats.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">En 3 Étapes vers le Succès</h2>
              <p className="text-xl text-muted-foreground">
                Du setup aux demandes qualifiées en moins de 30 minutes.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              {steps.map((step, index) => (
                <div key={index} className={`flex flex-col md:flex-row gap-6 items-center mb-12 ${index === steps.length - 1 ? 'mb-0' : ''}`}>
                  <div className="flex-shrink-0 w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-bold">
                    {step.number}
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-2xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-lg text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-4">Prêt pour Google Ads Intelligents?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Des milliers d'entrepreneurs français automatisent déjà leurs Google Ads. Pas d'agences. Pas de complexité. Juste des résultats.
            </p>
            <div className="flex justify-center mb-6">
              <Button
                size="lg"
                className="text-lg px-8 h-14 bg-white text-blue-600 hover:bg-gray-100 font-semibold"
                asChild
              >
                <Link to="/auth?signup=true">Essai Gratuit</Link>
              </Button>
            </div>
            <p className="text-sm opacity-80">Configuration Simple • Campagnes Professionnelles • Support Français</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FrancePage;
